/**
 * Story 24.25 — the ONE table of navigation icons (DESIGN.md › Navigation
 * icons). Outline at rest, solid while the row is active/selected.
 *
 * The Sidebar (and its rail), the BottomTabBar, the phone More page and
 * `adminNavigation` (Settings row, both Your team rows, Prompt Management —
 * the Engineering list) read their icons from here only. They must never
 * import one of these glyphs from `@heroicons/*` directly:
 * `src/lib/ui/__tests__/navIcons.test.ts` pins the table and that rule.
 */
import {
  BookOpenIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
  CommandLineIcon,
  EllipsisHorizontalIcon,
  QuestionMarkCircleIcon,
  Squares2X2Icon,
  UserGroupIcon,
  WrenchIcon,
} from '@heroicons/react/24/outline';
import {
  BookOpenIcon as BookOpenIconSolid,
  CalendarIcon as CalendarIconSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  CommandLineIcon as CommandLineIconSolid,
  EllipsisHorizontalIcon as EllipsisHorizontalIconSolid,
  QuestionMarkCircleIcon as QuestionMarkCircleIconSolid,
  Squares2X2Icon as Squares2X2IconSolid,
  UserGroupIcon as UserGroupIconSolid,
  WrenchIcon as WrenchIconSolid,
} from '@heroicons/react/24/solid';

export type NavIconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

export interface NavIconPair {
  /** At rest. */
  outline: NavIconComponent;
  /** Selected / active. */
  solid: NavIconComponent;
}

export type NavIconKey =
  | 'chats'
  | 'tasks'
  | 'boards'
  | 'more'
  | 'calendar'
  | 'library'
  | 'documentation'
  | 'settings'
  | 'yourTeam'
  | 'engineering'
  | 'promptManagement';

export const NAV_ICONS: Readonly<Record<NavIconKey, NavIconPair>> = Object.freeze({
  chats: { outline: ChatBubbleLeftRightIcon, solid: ChatBubbleLeftRightIconSolid },
  tasks: { outline: CheckCircleIcon, solid: CheckCircleIconSolid },
  boards: { outline: Squares2X2Icon, solid: Squares2X2IconSolid },
  more: { outline: EllipsisHorizontalIcon, solid: EllipsisHorizontalIconSolid },
  calendar: { outline: CalendarIcon, solid: CalendarIconSolid },
  library: { outline: BookOpenIcon, solid: BookOpenIconSolid },
  documentation: { outline: QuestionMarkCircleIcon, solid: QuestionMarkCircleIconSolid },
  settings: { outline: Cog6ToothIcon, solid: Cog6ToothIconSolid },
  yourTeam: { outline: UserGroupIcon, solid: UserGroupIconSolid },
  engineering: { outline: WrenchIcon, solid: WrenchIconSolid },
  promptManagement: { outline: CommandLineIcon, solid: CommandLineIconSolid },
});
